/** Digits only, with a leading "FI" stripped if present. */
export const normalizeFinnishAccountDigits = (value: string): string =>
	value.replace(/\s|-/g, "").replace(/^FI/i, "").replace(/\D/g, "");

/** ISO 13616 check digits for a 14-digit Finnish BBAN. "FI" → F=15, I=18. */
export const finnishIbanCheckDigits = (bban: string): string => {
	let remainder = 0;
	for (const char of `${bban}151800`) {
		remainder = (remainder * 10 + Number(char)) % 97;
	}
	return String(98 - remainder).padStart(2, "0");
};

/**
 * Builds a full Finnish IBAN. 16 digits are treated as check digits + account
 * and kept as-is; 14 digits get computed check digits. Anything else is
 * returned untouched so the backend sees exactly what FLOW/Tink provided.
 */
export const buildFinnishIban = (value: string): string => {
	const accountDigits = normalizeFinnishAccountDigits(value);
	if (accountDigits.length === 16) return `FI${accountDigits}`;
	if (accountDigits.length === 14) {
		return `FI${finnishIbanCheckDigits(accountDigits)}${accountDigits}`;
	}
	return value.trim();
};

/** Characters a typed account number may have, spaces ignored: an FI IBAN. */
export const FINNISH_ACCOUNT_LENGTH = { min: 18, max: 18 };

export type FinnishAccountIssue = "mandatory" | "invalid";

/**
 * Only the length of a typed account is checked. A prefilled (Tink/FLOW)
 * account is read-only and trusted as-is, whatever its format.
 */
export const findFinnishAccountIssue = ({
	accountNumber,
	prefilledAccountNumber,
}: {
	accountNumber: string;
	prefilledAccountNumber: string;
}): FinnishAccountIssue | undefined => {
	if (prefilledAccountNumber) return undefined;

	const { length } = accountNumber.replace(/\s/g, "");
	if (length === 0) return "mandatory";

	const { min, max } = FINNISH_ACCOUNT_LENGTH;
	return length < min || length > max ? "invalid" : undefined;
};
